import { useMemo } from "react";
import { clearImpersonateParam } from "@src/features/app/hooks/usePreserveImpersonateParam";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { useIsStaff } from "@src/features/app/hooks/useIsStaff";
import useUser from "@src/features/app/hooks/useUser";
import { Ff, GraphqlOperations, UserType } from "@src/types.generated";

interface ImpersonatedUser {
  id: number;
  name: string;
  userType: string;
}

interface StartImpersonatingOptions {
  redirectTo?: string;
}

const URL_PARAM = "impersonate";

function parseImpersonateParam(value: string): ImpersonatedUser | null {
  const dashIndex = value.indexOf("-");
  if (dashIndex === -1) return null;
  const id = parseInt(value.substring(0, dashIndex), 10);
  const name = value.substring(dashIndex + 1);
  if (isNaN(id) || !name) return null;
  return { id, name, userType: "" };
}

function formatImpersonateParam(user: ImpersonatedUser): string {
  return `${user.id}-${user.name}`;
}

/** Parse the impersonate user ID from the current URL. Used by Apollo link. */
export function getImpersonateUserIdFromUrl(): number | null {
  const user = parseImpersonateParam(
    new URLSearchParams(window.location.search).get(URL_PARAM) ?? "",
  );
  return user?.id ?? null;
}

/** Operations that bypass impersonation and always run as the real user. */
export const IMPERSONATION_EXEMPT_OPERATIONS: ReadonlySet<string> = new Set([
  GraphqlOperations.Query.SearchUsersForImpersonation,
  GraphqlOperations.Query.IsStaff,
]);

export function startImpersonating(
  user: ImpersonatedUser,
  options?: StartImpersonatingOptions,
): void {
  const target = options?.redirectTo ?? window.location.pathname;
  const url = new URL(target, window.location.origin);
  if (!options?.redirectTo) {
    url.search = window.location.search;
  }
  url.searchParams.set(URL_PARAM, formatImpersonateParam(user));
  window.location.href = url.toString();
}

export function stopImpersonating(): void {
  clearImpersonateParam();
  const url = new URL(window.location.href);
  url.searchParams.delete(URL_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
  window.location.reload();
}

export function useCanImpersonate(): boolean {
  const impersonationEnabled = useFeatureFlag(Ff.Impersonation);
  const { isStaff } = useIsStaff();
  const { user } = useUser();
  return (
    !!impersonationEnabled && (isStaff || user?.userType === UserType.Admin)
  );
}

export function useImpersonation(): {
  impersonatedUser: { id: number; name: string; userType: string } | null;
  isImpersonating: boolean;
} {
  // Intentionally mount-only: start/stop both trigger full page reloads,
  // so the URL is always fresh on mount.
  const impersonatedUser = useMemo(() => {
    if (typeof window === "undefined") return null;
    const value = new URLSearchParams(window.location.search).get(URL_PARAM);
    if (!value) return null;
    return parseImpersonateParam(value);
  }, []);

  const isImpersonating = impersonatedUser !== null;

  return { impersonatedUser, isImpersonating };
}
